"use client";

import { memo, useEffect, useState } from "react";
import { Wallet } from "lucide-react";
import { resolveSymbolIcon } from "@/lib/symbol-icons/resolve";
import { useSymbolLive } from "@/lib/symbol-icons/liveness";
import { useSymbolsStore } from "@/vybe-trader-client/symbols/stores/useSymbolsStore";

type Sheet = "core" | "stocks";
const sheets: Record<Sheet, { ready: boolean; loading: Promise<void> | null; load: () => Promise<{ SYMBOL_ICON_SPRITE: string }> }> = {
  core: { ready: false, loading: null, load: () => import("@/lib/symbol-icons/sprite.generated") },
  stocks: { ready: false, loading: null, load: () => import("@/lib/symbol-icons/sprite-stocks.generated") },
};
const sheetOf = (key: string | null): Sheet => (key?.startsWith("stock:") ? "stocks" : "core");

/** Loads a sprite chunk once and parks it in the document so every icon can <use> it. */
function ensureSprite(name: Sheet): Promise<void> {
  const sheet = sheets[name];
  if (sheet.ready) return Promise.resolve();
  sheet.loading ??= sheet.load().then(({ SYMBOL_ICON_SPRITE }) => {
    const id = `vybe-symbol-icon-sprite-${name}`;
    if (!document.getElementById(id)) {
      const host = document.createElement("div");
      host.id = id;
      // Not display:none — masks and gradients inside a hidden sprite stop rendering in some engines.
      host.style.cssText = "position:absolute;width:0;height:0;overflow:hidden";
      host.innerHTML = SYMBOL_ICON_SPRITE;
      document.body.appendChild(host);
    }
    sheet.ready = true;
  });
  return sheet.loading;
}

const symbolIdOf = (key: string) => `si-${key.replace(":", "-").toLowerCase()}`;

export interface SymbolIconProps {
  /** Symbol name as the broker spells it (`Symbol.currency`). */
  name: string;
  /** Group path (`Symbol.groupName`). */
  group?: string;
  description?: string;
  /** False greys the icon out: the symbol is not streaming. */
  live?: boolean;
  /** Diameter in px. */
  size?: number;
  className?: string;
}

function SymbolIconInner({ name, group, description, live = true, size = 14, className = "" }: SymbolIconProps) {
  const icon = resolveSymbolIcon({ name, group, description });
  const sheet = sheetOf(icon.base);
  const [, bump] = useState(0);
  const ready = sheets[sheet].ready && sheets.core.ready;
  useEffect(() => {
    if (ready) return;
    let cancelled = false;
    void Promise.all([ensureSprite("core"), ensureSprite(sheet)]).then(() => {
      if (!cancelled) bump((n) => n + 1);
    });
    return () => {
      cancelled = true;
    };
  }, [ready, sheet]);

  const badge = Math.round(size * 0.57);

  return (
    <span
      aria-hidden="true"
      data-symbol-icon={icon.cls}
      data-live={live ? "true" : "false"}
      className={`relative inline-block shrink-0 transition-[filter,opacity] duration-300 ${
        live ? "" : "opacity-70 grayscale"
      } ${className}`}
      style={{ width: size, height: size, marginRight: icon.quote ? Math.round(badge / 3) : 0 }}
    >
      {icon.base ? (
        <svg
          width={size}
          height={size}
          className="block rounded-full bg-muted ring-1 ring-border"
          // Inline size: buttons and menu items restyle every descendant <svg> (`[&_svg]:size-4`).
          style={{ width: size, height: size }}
        >
          {ready && <use href={`#${symbolIdOf(icon.base)}`} />}
        </svg>
      ) : (
        <span
          className="flex items-center justify-center rounded-full bg-muted font-sans font-semibold leading-none text-muted-foreground"
          style={{ width: size, height: size, fontSize: Math.round(size * (size >= 22 ? 0.4 : 0.57)) }}
        >
          {size >= 22 ? icon.monogram : icon.monogram.charAt(0)}
        </span>
      )}
      {icon.base && icon.quote && (
        <svg
          width={badge}
          height={badge}
          className="absolute rounded-full bg-muted ring-1 ring-background"
          style={{ width: badge, height: badge, right: -Math.round(badge / 3), bottom: -1 }}
        >
          {ready && <use href={`#${symbolIdOf(icon.quote)}`} />}
        </svg>
      )}
    </span>
  );
}

/** A symbol's icon. Purely presentational — pass `live` to grey it out. */
export const SymbolIcon = memo(SymbolIconInner);

/** A watchlisted symbol's icon, in colour while it ticks and grey once it has been still for two minutes. */
export const LiveSymbolIcon = memo(function LiveSymbolIcon(props: Omit<SymbolIconProps, "live">) {
  const live = useSymbolLive(props.name);
  return <SymbolIcon {...props} live={live} />;
});

type NamedSymbolIconProps = Pick<SymbolIconProps, "name" | "size" | "className">;

/**
 * For rows that know only a symbol's name (positions, orders, deals): the group and description
 * the rules may need come from the catalog. Renders nothing for a row with no symbol (a deposit).
 */
export const NamedSymbolIcon = memo(function NamedSymbolIcon({ name, ...rest }: NamedSymbolIconProps) {
  const symbol = useSymbolsStore((state) => state.byCurrency.get(name));
  if (!name) return null;
  return <SymbolIcon name={name} group={symbol?.groupName} description={symbol?.description} {...rest} />;
});

/** The same, greyed out while the symbol is not streaming — for open positions. */
export const LiveNamedSymbolIcon = memo(function LiveNamedSymbolIcon({ name, ...rest }: NamedSymbolIconProps) {
  const symbol = useSymbolsStore((state) => state.byCurrency.get(name));
  const live = useSymbolLive(name);
  if (!name) return null;
  return <SymbolIcon name={name} group={symbol?.groupName} description={symbol?.description} live={live} {...rest} />;
});

/**
 * The leading mark of a two-line list row (mobile positions, orders, deals). A row with no symbol —
 * a deposit, a withdrawal — gets a neutral wallet so every row keeps the same left edge.
 */
export const SymbolAvatar = memo(function SymbolAvatar({
  name,
  track = false,
  size = 28,
}: {
  name?: string | null;
  /** Grey out while the symbol is not streaming (open positions). */
  track?: boolean;
  size?: number;
}) {
  if (!name) {
    return (
      <span
        aria-hidden="true"
        className="flex shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground"
        style={{ width: size, height: size }}
      >
        <Wallet style={{ width: size / 2, height: size / 2 }} />
      </span>
    );
  }
  return track ? <LiveNamedSymbolIcon name={name} size={size} /> : <NamedSymbolIcon name={name} size={size} />;
});
